import React from "react";
import {Box, Checkbox, IconButton, Tooltip} from "@mui/material";
import {Close, DescriptionOutlined, FilterAltOutlined, PlaylistAddCheckOutlined, TuneOutlined} from "@mui/icons-material";
import type {GridColDef, GridRowId} from "@mui/x-data-grid-premium";
import {DataGridPremium} from "@mui/x-data-grid-premium";
import type {useGridApiRef} from "@mui/x-data-grid-premium";
import type {ContractChild, ContractParent} from "../contractCoverageData";
import {COVERAGE_CURRENCY} from "../contractCoverageData";
import type {CoverageFilterKey} from "../contractCoverageUtils";
import {
    actionForPosition,
    childMatchesFilter,
    FILTER_LABEL,
    formatCompactCurrency,
    formatNumber,
    formatPercent,
    POSITION_LABEL,
    POSITION_ORDER,
    positionColors,
    utilizationColor,
} from "../contractCoverageUtils";
import type {ScoreOverrides} from "../scoringModel";
import {recomputeParent} from "../scoringModel";
import {ExecutionPlanDrawer} from "./ExecutionPlanDrawer";
import {OverviewDrawer} from "./OverviewDrawer";
import {ScoreReviewDrawer} from "./ScoreReviewDrawer";

export type CoverageGridApiRef = ReturnType<typeof useGridApiRef>;

type Props = {
    rows: ContractParent[];
    apiRef: CoverageGridApiRef;
    activeFilter: CoverageFilterKey | null;
    onClearFilter: () => void;
    loading?: boolean;
};

const overrideKey = (parentId: string, childIndex: number) => `${parentId}::${childIndex}`;

export const ContractCoverageTable: React.FC<Props> = ({rows, apiRef, activeFilter, onClearFilter, loading = false}) => {
    // Human-in-the-loop score overrides, keyed by "<parentId>::<childIndex>".
    const [overrides, setOverrides] = React.useState<Map<string, ScoreOverrides>>(new Map());
    // Controlled so applying an override doesn't collapse the open panel on re-render/re-sort.
    const [expandedIds, setExpandedIds] = React.useState<Set<GridRowId>>(new Set());

    const originalById = React.useMemo(() => new Map(rows.map(p => [p.id, p])), [rows]);

    const parentOverrides = React.useCallback((parentId: string) => {
        const m = new Map<number, ScoreOverrides>();
        overrides.forEach((v, k) => {
            const [pid, idx] = k.split('::');
            if (pid === parentId) m.set(Number(idx), v);
        });
        return m;
    }, [overrides]);

    // Effective rows: original data with overrides applied and parent totals re-summed.
    const effectiveRows = React.useMemo(
        () => rows.map(p => recomputeParent(p, parentOverrides(p.id))),
        [rows, parentOverrides]);

    // Filter: keep parents with at least one matching child.
    const filteredRows = React.useMemo(
        () => activeFilter
            ? effectiveRows.filter(p => p.children.some(ch => childMatchesFilter(ch, activeFilter)))
            : effectiveRows,
        [effectiveRows, activeFilter]);

    const matchCount = React.useMemo(
        () => activeFilter
            ? filteredRows.reduce((s, p) => s + p.children.filter(ch => childMatchesFilter(ch, activeFilter)).length, 0)
            : 0,
        [filteredRows, activeFilter]);

    // Auto-expand matching groups when a filter is applied; collapse when cleared.
    const [prevFilter, setPrevFilter] = React.useState(activeFilter);
    if (prevFilter !== activeFilter) {
        setPrevFilter(activeFilter);
        setExpandedIds(activeFilter ? new Set(filteredRows.map(p => p.id)) : new Set());
    }

    const applyChildOverrides = React.useCallback((parentId: string, childIndex: number, o: ScoreOverrides) => {
        setOverrides(prev => {
            const next = new Map(prev);
            const k = overrideKey(parentId, childIndex);
            if (Object.keys(o).length === 0) next.delete(k); else next.set(k, o);
            return next;
        });
    }, []);

    const columns = React.useMemo<GridColDef<ContractParent>[]>(() => [
        {
            field: 'name',
            headerName: 'Parent supplier',
            flex: 2.4,
            minWidth: 300,
            renderCell: ({row}) => (
                <div className="cci-supplier-cell">
                    <div className="cci-supplier-name">{row.name}</div>
                    <div className="cci-supplier-meta">
                        {row.country && <span className="cci-country-chip">{row.country}</span>}
                        <span>{formatNumber(row.childCount)} supplier{row.childCount === 1 ? '' : 's'}</span>
                        {row.contractedChildren > 0 &&
                            <span>· {formatNumber(row.contractedChildren)} contracted</span>}
                    </div>
                </div>
            ),
        },
        {
            field: 'spend',
            headerName: 'Cumulative spend',
            type: 'number',
            flex: 1,
            minWidth: 150,
            renderCell: ({row}) => (
                <span className="cci-num cci-spend">{formatCompactCurrency(row.spend, COVERAGE_CURRENCY)}</span>
            ),
        },
        {
            field: 'costSavingEur',
            headerName: 'Cost saving',
            type: 'number',
            flex: 1,
            minWidth: 140,
            renderCell: ({row}) =>
                row.costSavingEur > 0
                    ? <span className="cci-num cci-saving">{formatCompactCurrency(row.costSavingEur, COVERAGE_CURRENCY)}</span>
                    : <span className="cci-num cci-muted">—</span>,
        },
        {
            field: 'costAvoidanceEur',
            headerName: 'Cost avoidance',
            type: 'number',
            flex: 1,
            minWidth: 140,
            renderCell: ({row}) =>
                row.costAvoidanceEur > 0
                    ? <span className="cci-num cci-avoidance">{formatCompactCurrency(row.costAvoidanceEur, COVERAGE_CURRENCY)}</span>
                    : <span className="cci-num cci-muted">—</span>,
        },
    ], []);

    return (
        <Box sx={{width: '100%'}}>
            {activeFilter && (
                <div className="cci-filterbar">
                    <span className="cci-filter-chip">
                        <FilterAltOutlined fontSize="inherit"/>
                        {FILTER_LABEL[activeFilter]}
                        <span className="cci-filter-count">
                            {formatNumber(matchCount)} supplier{matchCount === 1 ? '' : 's'} · {formatNumber(filteredRows.length)} group{filteredRows.length === 1 ? '' : 's'}
                        </span>
                        <button type="button" className="cci-filter-clear" onClick={onClearFilter} aria-label="Clear filter">
                            <Close fontSize="inherit"/>
                        </button>
                    </span>
                </div>
            )}
            <DataGridPremium
                className="base-table-muix auto-row-height-centered"
                apiRef={apiRef}
                rows={filteredRows}
                columns={columns}
                loading={loading}
                getRowId={row => row.id}
                getRowHeight={() => 'auto'}
                getDetailPanelContent={({row}) => (
                    <ParentDetailPanel
                        effectiveParent={row}
                        originalParent={originalById.get(row.id) ?? row}
                        filter={activeFilter}
                        getChildOverrides={i => parentOverrides(row.id).get(i) ?? {}}
                        onApplyChild={(i, o) => applyChildOverrides(row.id, i, o)}
                    />
                )}
                getDetailPanelHeight={() => 'auto'}
                detailPanelExpandedRowIds={expandedIds}
                onDetailPanelExpandedRowIdsChange={setExpandedIds}
                headerFilters
                disableRowSelectionOnClick
                disableAggregation
                disableRowGrouping
                pagination
                pageSizeOptions={[10, 25, 50, 100]}
                initialState={{
                    pagination: {paginationModel: {pageSize: 25, page: 0}},
                    sorting: {sortModel: [{field: 'costSavingEur', sort: 'desc'}]},
                }}
                sx={{
                    border: 'none',
                    '--rowBorderColor': 'rgba(0,0,0,0.08)',
                    '& .MuiDataGrid-columnHeaderTitle': {fontWeight: 700},
                }}
            />
        </Box>
    );
};

type DetailProps = {
    effectiveParent: ContractParent;
    originalParent: ContractParent;
    filter: CoverageFilterKey | null;
    getChildOverrides: (childIndex: number) => ScoreOverrides;
    onApplyChild: (childIndex: number, overrides: ScoreOverrides) => void;
};

const ParentDetailPanel: React.FC<DetailProps> = ({effectiveParent, originalParent, filter, getChildOverrides, onApplyChild}) => {
    // Display rows pair the effective child (shown) with its original index (for overrides).
    // When a filter is active, only matching children are shown.
    const display = React.useMemo(
        () => effectiveParent.children
            .map((eff, idx) => ({eff, idx}))
            .filter(({eff}) => !filter || childMatchesFilter(eff, filter))
            .sort((a, b) =>
                POSITION_ORDER[a.eff.position] - POSITION_ORDER[b.eff.position] || b.eff.spend - a.eff.spend),
        [effectiveParent.children, filter]);

    const [selected, setSelected] = React.useState<Set<number>>(new Set());
    const [planOpen, setPlanOpen] = React.useState(false);
    const [overviewOpen, setOverviewOpen] = React.useState(false);
    const [scoreIdx, setScoreIdx] = React.useState<number | null>(null);

    const countries = React.useMemo(() => {
        const m = new Map<string, number[]>();
        effectiveParent.children.forEach((ch, i) => {
            if (ch.country) (m.get(ch.country) ?? m.set(ch.country, []).get(ch.country)!).push(i);
        });
        return Array.from(m, ([code, idx]) => ({code, idx})).sort((a, b) => b.idx.length - a.idx.length);
    }, [effectiveParent.children]);

    const toggle = (i: number) => setSelected(prev => {
        const next = new Set(prev);
        if (next.has(i)) next.delete(i); else next.add(i);
        return next;
    });
    const setMany = (idx: number[], on: boolean) => setSelected(prev => {
        const next = new Set(prev);
        idx.forEach(i => on ? next.add(i) : next.delete(i));
        return next;
    });
    const allIdx = effectiveParent.children.map((_, i) => i);
    const allSelected = selected.size === allIdx.length && allIdx.length > 0;

    // The contract analysis overview is offered once every child of that supplier is in the selection.
    const overviewEligible = allSelected;

    const selectedChildren = React.useMemo(
        () => effectiveParent.children.filter((_, i) => selected.has(i)), [effectiveParent.children, selected]);
    const selSpend = selectedChildren.reduce((s, c) => s + c.spend, 0);
    const selSaving = selectedChildren.reduce((s, c) => s + c.costSavingEur, 0);
    const selAvoid = selectedChildren.reduce((s, c) => s + c.costAvoidanceEur, 0);

    return (
        <div className="cci-detail">
            <div className="cci-detail-facts">
                <Fact label="Suppliers" value={formatNumber(effectiveParent.childCount)}/>
                <Fact label="Contracted suppliers" value={formatNumber(effectiveParent.contractedChildren)}/>
                <Fact label="Cumulative spend" value={formatCompactCurrency(effectiveParent.spend, COVERAGE_CURRENCY)}/>
                <Fact label="Cost saving" value={effectiveParent.costSavingEur > 0 ? formatCompactCurrency(effectiveParent.costSavingEur, COVERAGE_CURRENCY) : '—'} tone="saving"/>
                <Fact label="Cost avoidance" value={effectiveParent.costAvoidanceEur > 0 ? formatCompactCurrency(effectiveParent.costAvoidanceEur, COVERAGE_CURRENCY) : '—'} tone="avoidance"/>
            </div>

            {countries.length > 0 && (
                <div className="cci-quickselect">
                    <span className="cci-quickselect-label">Group for execution:</span>
                    {countries.map(({code, idx}) => {
                        const on = idx.every(i => selected.has(i));
                        return (
                            <button key={code} type="button" className={`cci-quick-chip${on ? ' is-on' : ''}`}
                                    onClick={() => setMany(idx, !on)}>
                                {code} <span className="cci-quick-count">{idx.length}</span>
                            </button>
                        );
                    })}
                    <button type="button" className="cci-quick-link" onClick={() => setMany(allIdx, !allSelected)}>
                        {allSelected ? 'Clear all' : 'Select all'}
                    </button>
                </div>
            )}

            <div className="cci-children-scroll">
                <table className="cci-children">
                    <thead>
                        <tr>
                            <th className="cci-children-check">
                                <Checkbox size="small" checked={allSelected}
                                          indeterminate={selected.size > 0 && !allSelected}
                                          onChange={(_e, on) => setMany(allIdx, on)}/>
                            </th>
                            <th>Supplier</th>
                            <th>Contract position</th>
                            <th className="cci-children-num">Spend</th>
                            <th className="cci-children-num">Transactions</th>
                            <th className="cci-children-num">Entities</th>
                            <th className="cci-children-num">Active months</th>
                            <th className="cci-children-num">Contracted value</th>
                            <th className="cci-children-num">Utilisation</th>
                            <th className="cci-children-num">Cost saving</th>
                            <th className="cci-children-num">Cost avoidance</th>
                            <th className="cci-children-action">Scoring</th>
                        </tr>
                    </thead>
                    <tbody>
                        {display.map(({eff, idx}) => (
                            <ChildRow
                            key={idx}
                            ch={eff}
                            checked={selected.has(idx)}
                            edited={Object.keys(getChildOverrides(idx)).length > 0}
                            onToggle={() => toggle(idx)}
                            onReview={() => setScoreIdx(idx)}
                            />
                        ))}
                    </tbody>
                </table>
            </div>

            {selected.size > 0 && (
                <div className="cci-selbar">
                    <span className="cci-selbar-summary">
                        <strong>{selected.size}</strong> selected
                        <span className="cci-selbar-sep">·</span>{formatCompactCurrency(selSpend, COVERAGE_CURRENCY)}
                        {selSaving > 0 && <><span className="cci-selbar-sep">·</span>saving <span className="cci-saving">{formatCompactCurrency(selSaving, COVERAGE_CURRENCY)}</span></>}
                        {selAvoid > 0 && <><span className="cci-selbar-sep">·</span>avoidance <span className="cci-avoidance">{formatCompactCurrency(selAvoid, COVERAGE_CURRENCY)}</span></>}
                    </span>
                    <span className="cci-selbar-actions">
                        <button type="button" className="cci-selbar-clear" onClick={() => setSelected(new Set())}>Clear</button>
                        {overviewEligible && (
                            <button type="button" className="cci-selbar-overview" onClick={() => setOverviewOpen(true)}>
                                <DescriptionOutlined fontSize="small"/>
                                Generate overview
                            </button>
                        )}
                        <button type="button" className="cci-selbar-build" onClick={() => setPlanOpen(true)}>
                            <PlaylistAddCheckOutlined fontSize="small"/>
                            Build execution plan
                        </button>
                    </span>
                </div>
            )}

            <ExecutionPlanDrawer
                open={planOpen}
                onClose={() => setPlanOpen(false)}
                parent={effectiveParent}
                children={selectedChildren}
            />

            <OverviewDrawer
                open={overviewOpen}
                onClose={() => setOverviewOpen(false)}
                parentId={effectiveParent.id}
            />

            <ScoreReviewDrawer
                open={scoreIdx !== null}
                onClose={() => setScoreIdx(null)}
                child={scoreIdx !== null ? originalParent.children[scoreIdx] : null}
                overrides={scoreIdx !== null ? getChildOverrides(scoreIdx) : {}}
                onApply={o => {
                    if (scoreIdx !== null) onApplyChild(scoreIdx, o);
                }}
            />
        </div>
    );
};

const ChildRow: React.FC<{
    ch: ContractChild;
    checked: boolean;
    edited: boolean;
    onToggle: () => void;
    onReview: () => void;
}> = ({ch, checked, edited, onToggle, onReview}) => {
    const c = positionColors(ch.position);
    const hasValue = ch.contractedValueEur > 0;
    return (
        <tr className={checked ? 'is-selected' : undefined} title={actionForPosition(ch.position)}>
            <td className="cci-children-check">
                <Checkbox size="small" checked={checked} onChange={onToggle}/>
            </td>
            <td>
                {ch.supplier}
                {ch.country && <span className="cci-country-chip cci-inline">{ch.country}</span>}
                {edited && <span className="cci-edited-chip">Adjusted</span>}
            </td>
            <td>
                <span className="cci-position-pill" style={{backgroundColor: c.bg, color: c.fg}}>
                    {POSITION_LABEL[ch.position]}
                </span>
            </td>
            <td className="cci-children-num">{formatCompactCurrency(ch.spend, COVERAGE_CURRENCY)}</td>
            <td className="cci-children-num">{formatNumber(ch.transactions)}</td>
            <td className="cci-children-num">{formatNumber(ch.entities)}</td>
            <td className="cci-children-num">{formatNumber(ch.activeMonths)}</td>
            <td className="cci-children-num">
                {hasValue ? formatCompactCurrency(ch.contractedValueEur, COVERAGE_CURRENCY) : '—'}
            </td>
            <td className="cci-children-num">
                {hasValue
                    ? <span style={{color: utilizationColor(ch.utilizationPct)}}>{formatPercent(ch.utilizationPct)}</span>
                    : '—'}
            </td>
            <td className="cci-children-num">
                {ch.costSavingEur > 0
                    ? <span className="cci-saving">{formatCompactCurrency(ch.costSavingEur, COVERAGE_CURRENCY)}</span>
                    : '—'}
            </td>
            <td className="cci-children-num">
                {ch.costAvoidanceEur > 0
                    ? <span className="cci-avoidance">{formatCompactCurrency(ch.costAvoidanceEur, COVERAGE_CURRENCY)}</span>
                    : '—'}
            </td>
            <td className="cci-children-action">
                <Tooltip title="Review & adjust scoring" arrow>
                    <IconButton size="small" onClick={onReview}>
                        <TuneOutlined fontSize="small"/>
                    </IconButton>
                </Tooltip>
            </td>
        </tr>
    );
};

const Fact: React.FC<{label: string; value: string; tone?: 'saving' | 'avoidance'}> = ({label, value, tone}) => (
    <div className="cci-fact">
        <span className="cci-fact-label">{label}</span>
        <span className={`cci-fact-value${tone ? ` is-${tone}` : ''}`}>{value}</span>
    </div>
);
